<template>
  <div class="form-email-send-panel d-flex flex-column">

    <!-- Nothing is drawn until the allowance is known, or the compose form flashes up for a
         moment on accounts whose plan does not carry the feature. -->
    <div v-if="!usageLoaded" class="d-flex justify-center align-center py-16">
      <v-progress-circular indeterminate color="brand-color" size="28" />
    </div>

    <!-- Not on this plan: the API says the feature carries no allowance at all for this account -->
    <v-sheet v-else-if="featureNotInPlan" rounded="lg" class="pa-8 text-center bg-brand-color-lighten border-thin my-6">
      <v-icon size="32" color="brand-color" class="mb-3">mdi-email-lock-outline</v-icon>
      <p class="text-body-large font-weight-semibold text-brand-color mb-1">Sending forms by email</p>
      <p class="text-body-medium text-medium-emphasis mb-5">
        This is not included in your current plan. Upgrade to unlock it — the share link, embed code
        and QR code on the other tabs still work as usual.
      </p>
      <v-btn color="brand-color" class="text-none" :to="{ name: 'plans.index' }">Upgrade to unlock</v-btn>
    </v-sheet>

    <template v-else>

    <!-- Compose / Sent, with sender settings alongside while composing -->
    <div class="d-flex align-center justify-space-between flex-wrap ga-3 my-6">
      <segmented-toggle v-model="innerTab" :items="innerTabs" label="Compose or sent emails" />

      <!-- Red when the sender name or reply-to is missing or invalid, since that disables sending. -->
      <v-btn v-if="innerTab === INNER_TAB.COMPOSE" size="small" variant="tonal"
        :color="senderValid ? 'accent-color' : 'error'" class="text-none"
        :prepend-icon="senderValid ? 'mdi-cog-outline' : 'mdi-alert-decagram'" @click="openSenderDialog">
        Sender settings
      </v-btn>
    </div>

    <!-- Compose -->
    <div v-show="innerTab === INNER_TAB.COMPOSE">
      <v-row density="compact">
        <v-col cols="12" md="6">
          <v-select v-model="selectedLinkId" label="Link to Send" :items="linkOptions" item-title="title"
            item-value="value" density="comfortable" chips :loading="loadingLinks" :hint="previewUrl" persistent-hint>
            <template #chip="{ internalItem: item }">
              <v-chip size="small">{{ item.title }}</v-chip>
            </template>
            <template #append-inner>
              <v-tooltip location="top" max-width="280" open-on-click>
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" size="14" color="grey-darken-2" role="button" tabindex="0"
                    aria-label="About this link" @mousedown.stop @click.stop>
                    mdi-information-slab-circle
                  </v-icon>
                </template>
                <span class="text-tooltip">{{ linkHelpText }}</span>
              </v-tooltip>
            </template>
            <template #message="{ message }">
              <span class="text-body-small">{{ message }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="subject" label="Subject" density="comfortable" maxlength="200"
            :rules="$valid([{ rule: 'required', msg: 'Please enter a subject' }, { rule: 'between', min: 3, max: 200 }])">
            <template #message="{ message }">
              <span class="text-body-small">{{ message }}</span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>

      <email-chips-input ref="recipientsField" v-model="recipientsText" label="Recipients"
        density="comfortable" required hint="Press Enter after each address, or paste several at once. Each person gets their own email."
        :counter="MAX_RECIPIENTS" :error-messages="recipientError ? [recipientError] : []" class="recipients-field" />

      <v-textarea v-model="message" label="Message" density="comfortable" rows="3" maxlength="2000" counter="2000"
        placeholder="Add a short note for the people you are emailing." />

      <v-alert v-if="formError" type="warning" variant="tonal" density="compact" rounded="lg" class="mt-4 text-body-medium"
        color="accent-color" icon="mdi-alert-circle-outline">
        {{ formError }}
      </v-alert>
    </div>

    <!-- Sent -->
    <div v-show="innerTab === INNER_TAB.SENT">
      <div class="d-flex align-center justify-space-between mb-3">
        <span class="text-body-small font-weight-bold text-medium-emphasis">RECENT SENDS</span>
        <v-btn variant="tonal" color="brand-color" size="small" class="text-none" prepend-icon="mdi-refresh"
          :loading="loadingSends" @click="loadSends()">
          Refresh
        </v-btn>
      </div>

      <div v-if="loadingSends" class="d-flex justify-center pa-6">
        <v-progress-circular indeterminate color="brand-color" size="24" />
      </div>
      <v-sheet v-else-if="sends.length === 0" rounded="lg" class="pa-4 bg-brand-color-lighten border-thin">
        <p class="text-body-medium text-medium-emphasis mb-0">Nothing sent yet.</p>
      </v-sheet>
      <v-expansion-panels v-else variant="accordion" elevation="0" rounded="lg" class="border rounded-lg">
        <v-expansion-panel v-for="send in sends" :key="send.id">
          <v-expansion-panel-title>
            <div class="d-flex flex-column">
              <span class="text-body-medium font-weight-medium">{{ send.subject }}</span>
              <span class="text-body-small text-medium-emphasis">
                {{ formatSentDate(send.created_at) }} · {{ countLabel(send.recipients_count, 'recipient', 'recipients')
                }}
              </span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="d-flex flex-wrap ga-2">
              <v-chip v-for="recipient in send.recipients" :key="recipient.id" size="small" label
                :color="statusColor(recipient.status)" variant="tonal">
                {{ recipient.email }} · {{ recipient.status }}
              </v-chip>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="sendsMeta.lastPage > 1" class="d-flex align-center justify-space-between flex-wrap ga-3 mt-4">
        <span class="text-body-small text-medium-emphasis">
          Showing {{ sendsMeta.from }}–{{ sendsMeta.to }} of {{ sendsMeta.total }}
        </span>
        <v-pagination v-model="sendsPage" :length="sendsMeta.lastPage" :total-visible="5" density="compact" size="small"
          color="brand-color" @update:model-value="loadSends" />
      </div>
    </div>

    <!-- Action bar, always visible -->
    <v-divider class="mt-5" />
    <div class="d-flex align-center justify-space-between flex-wrap ga-3 pt-4">
      <span v-if="usage.limit !== null" class="text-body-small text-medium-emphasis">
        {{ usage.remaining }} of {{ usage.limit }} form emails left{{ usagePeriodLabel }}
      </span>
      <span v-else class="text-body-small text-medium-emphasis"></span>
      <div class="d-flex ga-2">
        <v-btn variant="outlined" color="outlined-color" class="text-none" :loading="sendingTest" :disabled="!canSend"
          prepend-icon="mdi-email-check-outline" @click="submit(true)">
          Send test to myself
        </v-btn>
        <v-btn color="brand-color" class="text-none px-5" :loading="sending" :disabled="!canSend"
          prepend-icon="mdi-send" @click="submit(false)">
          {{ sendButtonLabel }}
        </v-btn>
      </div>
    </div>

    </template>

    <!-- Sender settings -->
    <v-dialog v-model="senderDialog.show" max-width="550" persistent>
      <v-card>
        <div class="card-section-header">
          <div class="d-flex align-center ga-3">
            <v-avatar color="brand-color" variant="tonal" size="40" rounded="lg">
              <v-icon color="brand-color" size="18">mdi-account-cog-outline</v-icon>
            </v-avatar>
            <h2 class="section-title">Sender Settings</h2>
          </div>
        </div>
        <v-card-text class="pa-6">
          <v-form ref="senderForm" @submit.prevent="applySenderDialog">
            <v-text-field v-model="senderDialog.name" label="Sender name" prepend-inner-icon="mdi-account-outline"
              density="comfortable" maxlength="100" hint="The name recipients see the email from." persistent-hint
              :rules="$valid(SENDER_NAME_RULES)" class="mb-4">
              <template #message="{ message }">
                <span class="text-body-small">{{ message }}</span>
              </template>
            </v-text-field>
            <v-text-field v-model="senderDialog.replyTo" label="Reply-to email" prepend-inner-icon="mdi-email-outline"
              density="comfortable" hint="Replies go to this address." persistent-hint
              validate-on="blur" :rules="[...$valid(REPLY_TO_RULES), disposableEmailRule]">
              <template #message="{ message }">
                <span class="text-body-small">{{ message }}</span>
              </template>
            </v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn color="outlined-color" variant="outlined" @click="senderDialog.show = false">
            Cancel
          </v-btn>
          <v-btn color="brand-color" variant="flat" @click="applySenderDialog">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="3000" location="bottom center" :color="snackbar.color" rounded="lg">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import { generateRules } from '@/plugins/validation/handler'
import { disposableEmailRule } from '@/utils/disposableEmail'
import EmailChipsInput from '@/components/EmailChipsInput.vue'
import SegmentedToggle from '@/components/common/SegmentedToggle.vue'
import { fetchUrlBuilderLinks } from '@/api/form-url-builder-links'
import {
  fetchFormEmailSends,
  fetchFormEmailUsage,
  sendFormByEmail,
  sendFormByEmailTest
} from '@/api/form-email-sends'

const MAX_RECIPIENTS = 50
// Anything a person might separate pasted addresses with.
const SENT_DATE_FORMAT = 'D MMM YYYY, h:mm a'

// One definition per field: the template feeds it to $valid and the code below to generateRules,
// so the send button and the fields never disagree. Reply-to follows the Form Settings To/Cc rules.
const SENDER_NAME_RULES = [
  { rule: 'required', msg: 'Please enter a sender name' },
  { rule: 'between', fieldName: 'Sender name', min: 3, max: 100 }
]
const REPLY_TO_RULES = [
  { rule: 'required', msg: 'Please enter a reply-to email' },
  { rule: 'email', fieldName: 'Reply-to email' },
  { rule: 'commaSeparatedEmails', msg: 'Please enter a valid reply-to email address' }
]
const senderNameRules = generateRules(SENDER_NAME_RULES)
const replyToRules = generateRules(REPLY_TO_RULES)
const emailListRules = generateRules([{ rule: 'commaSeparatedEmails' }])

const INNER_TAB = { COMPOSE: 'compose', SENT: 'sent' }
const SEND_STATUS = { SENT: 'sent', QUEUED: 'queued', FAILED: 'failed' }
const STATUS_COLORS = {
  [SEND_STATUS.SENT]: 'success',
  [SEND_STATUS.QUEUED]: 'info',
  [SEND_STATUS.FAILED]: 'error'
}
const DEFAULT_STATUS_COLOR = 'grey'
const RESET_PERIOD_LABELS = { MONTHLY: ' this month', YEARLY: ' this year' }
const SENDER_FIELD = { NAME: 'sender_name', REPLY_TO: 'reply_to' }
const PUBLISHED_LINK_OPTION = { title: 'Published link', value: null }
const LINK_HELP = {
  PUBLISHED: 'The public link to this form. Anyone who opens it sees the form exactly as published.',
  SAVED: 'A saved link from your URL Builder. It opens the form with the parameters saved on it, for example to pre-fill answers or track where a lead came from.'
}

const props = defineProps({
  formId: {
    type: Number,
    required: true
  },
  publishedUrl: {
    type: String,
    default: ''
  },
  formTitle: {
    type: String,
    default: ''
  }
})

const store = useStore()

const innerTab = ref(INNER_TAB.COMPOSE)
const senderForm = ref(null)
const recipientsField = ref(null)
const senderDialog = reactive({ show: false, name: '', replyTo: '' })
const recipients = ref([])
const recipientError = ref('')
const formError = ref('')
const subject = ref('')
const message = ref('')
const senderName = ref('')
const replyTo = ref('')
const selectedLinkId = ref(null)
const savedLinks = ref([])
const loadingLinks = ref(false)
const sends = ref([])
const sendsPage = ref(1)
const sendsMeta = ref({ total: 0, lastPage: 1, from: 0, to: 0 })
const loadingSends = ref(false)
const sending = ref(false)
const sendingTest = ref(false)
const usage = ref({ limit: null, used: 0, remaining: null, reset_period: null, allowed: true })
const usageLoaded = ref(false)
const snackbar = ref({ show: false, message: '', color: 'success' })

const user = computed(() => store.getters.user || {})

const innerTabs = computed(() => [
  { value: INNER_TAB.COMPOSE, text: 'Compose' },
  { value: INNER_TAB.SENT, text: 'Sent', count: sendsMeta.value.total }
])

const linkOptions = computed(() => [
  PUBLISHED_LINK_OPTION,
  ...savedLinks.value.map(link => ({ title: link.name, value: link.id }))
])

const selectedLink = computed(() => savedLinks.value.find(link => link.id === selectedLinkId.value) || null)

const linkHelpText = computed(() => selectedLink.value ? LINK_HELP.SAVED : LINK_HELP.PUBLISHED)

const previewUrl = computed(() => {
  const link = selectedLink.value
  if (!link) return props.publishedUrl

  const params = (link.params || []).filter(param => param.key && param.value)
  if (!params.length) return link.base_url

  const query = params
    .map(param => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value)}`)
    .join('&')
  return `${link.base_url}?${query}`
})

const validRecipients = computed(() => recipients.value.filter(isValidEmail))

const senderValid = computed(() =>
  passesRules(senderNameRules, senderName.value) && passesRules(replyToRules, replyTo.value)
)

const canSend = computed(() =>
  !recipientError.value &&
  validRecipients.value.length > 0 &&
  validRecipients.value.length === recipients.value.length &&
  recipients.value.length <= MAX_RECIPIENTS &&
  !!subject.value.trim() &&
  senderValid.value &&
  !sending.value &&
  !sendingTest.value
)

const sendButtonLabel = computed(() => {
  const count = validRecipients.value.length
  return count ? `Send to ${countLabel(count, 'person', 'people')}` : 'Send'
})

const usagePeriodLabel = computed(() => RESET_PERIOD_LABELS[usage.value.reset_period] || '')

// No limit AND not allowed means the plan carries no form-email feature at all, as opposed to
// having one that is used up. Taken from the API so the plan rules live in one place.
const featureNotInPlan = computed(() => usage.value.limit === null && usage.value.allowed === false)

onMounted(() => {
  applyDefaults()
  loadLinks()
  loadSends()
  loadUsage()
  focusRecipients()
})

watch(innerTab, (tab) => {
  if (tab === INNER_TAB.COMPOSE) focusRecipients()
})

// preventScroll: this panel mounts while the tab is still sliding in, and a plain focus() would scroll it sideways.
function focusRecipients() {
  nextTick(() => recipientsField.value?.$el.querySelector('input')?.focus({ preventScroll: true }))
}

function passesRules(rules, value) {
  return rules.every(rule => rule(value) === true)
}

function isValidEmail(email) {
  const value = String(email ?? '').trim()
  return value !== '' && passesRules(emailListRules, value)
}

function countLabel(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`
}

function statusColor(status) {
  return STATUS_COLORS[status] || DEFAULT_STATUS_COLOR
}

function formatSentDate(value) {
  return value ? moment(value).format(SENT_DATE_FORMAT) : ''
}

function showSnackbar(text, color = 'success') {
  snackbar.value = { show: true, message: text, color }
}

function senderStorageKey() {
  return `form_${props.formId}_email_sender`
}

// Sender name and reply-to are remembered per form so they are not retyped on every send.
function readRememberedSender() {
  try {
    return JSON.parse(localStorage.getItem(senderStorageKey())) || {}
  } catch (e) {
    return {}
  }
}

function rememberSender() {
  try {
    localStorage.setItem(senderStorageKey(), JSON.stringify({
      [SENDER_FIELD.NAME]: senderName.value,
      [SENDER_FIELD.REPLY_TO]: replyTo.value
    }))
  } catch (e) {
    console.warn('Could not remember sender settings for this form.', e)
  }
}

function applyDefaults() {
  const remembered = readRememberedSender()
  subject.value = props.formTitle ? `Please fill in ${props.formTitle}` : 'Please fill in our form'
  senderName.value = remembered[SENDER_FIELD.NAME] || user.value.name || ''
  replyTo.value = remembered[SENDER_FIELD.REPLY_TO] || user.value.email || ''
}

function openSenderDialog() {
  senderDialog.name = senderName.value
  senderDialog.replyTo = replyTo.value
  senderDialog.show = true
}

async function applySenderDialog() {
  const { valid } = await senderForm.value.validate()
  if (!valid) return

  senderName.value = senderDialog.name.trim()
  replyTo.value = senderDialog.replyTo.trim()
  rememberSender()
  senderDialog.show = false
}

const recipientsText = computed({
  get: () => recipients.value.join(','),
  set: (value) => {
    recipients.value = value.split(',').filter(Boolean)
    recipientError.value = recipients.value.length > MAX_RECIPIENTS
      ? `You can email up to ${MAX_RECIPIENTS} people in one send.`
      : ''
  }
})

async function loadLinks() {
  loadingLinks.value = true
  try {
    const response = await fetchUrlBuilderLinks(props.formId)
    savedLinks.value = response?.data?.data || []
  } catch (e) {
    console.warn('Could not load saved links for this form.', e)
    savedLinks.value = []
  } finally {
    loadingLinks.value = false
  }
}

async function loadSends(page = sendsPage.value) {
  loadingSends.value = true
  try {
    const response = await fetchFormEmailSends(props.formId, page)
    const paginator = response?.data?.data
    sends.value = paginator?.data || []
    sendsPage.value = paginator?.current_page || page
    sendsMeta.value = {
      total: paginator?.total || 0,
      lastPage: paginator?.last_page || 1,
      from: paginator?.from || 0,
      to: paginator?.to || 0
    }
  } catch (e) {
    console.warn('Could not load sent emails for this form.', e)
    sends.value = []
    sendsMeta.value = { total: 0, lastPage: 1, from: 0, to: 0 }
  } finally {
    loadingSends.value = false
  }
}

async function loadUsage() {
  try {
    const response = await fetchFormEmailUsage()
    const latest = response?.data?.data
    if (latest) usage.value = latest
  } catch (e) {
    console.warn('Could not load form email usage.', e)
  } finally {
    // Even a failed lookup has to release the panel, or it spins forever.
    usageLoaded.value = true
  }
}

async function submit(isTest) {
  if (!canSend.value) return
  formError.value = ''

  const recipientErrors = await recipientsField.value.validate()
  if (recipientErrors.length) return

  // The reply-to can come from saved settings without passing through the Sender dialog
  const replyToCheck = await disposableEmailRule(replyTo.value)
  if (replyToCheck !== true) {
    formError.value = `Reply-to email: ${replyToCheck} Change it in Sender settings.`
    return
  }

  const payload = {
    recipients: validRecipients.value,
    subject: subject.value.trim(),
    message: message.value.trim() || null,
    sender_name: senderName.value.trim(),
    reply_to: replyTo.value.trim(),
    form_url_builder_link_id: selectedLinkId.value
  }

  const busy = isTest ? sendingTest : sending
  busy.value = true

  try {
    if (isTest) {
      await sendFormByEmailTest(props.formId, payload)
      showSnackbar(`Test email sent to ${replyTo.value || user.value.email}.`)
    } else {
      await sendFormByEmail(props.formId, payload)
      showSnackbar(`Sent to ${countLabel(validRecipients.value.length, 'person', 'people')}.`)
      recipients.value = []
      message.value = ''
      await loadSends(1)
      innerTab.value = INNER_TAB.SENT
    }
    rememberSender()
    await loadUsage()
  } catch (e) {
    handleSendError(e)
  } finally {
    busy.value = false
  }
}

function handleSendError(error) {
  const meta = error?.response?.data?.meta
  const fieldErrors = meta?.errors

  if (fieldErrors?.reply_to?.length) {
    formError.value = `Reply-to email: ${fieldErrors.reply_to[0]} Change it in Sender settings.`
    return
  }
  // Laravel keys per-address errors as recipients.0, recipients.1, ...
  const recipientErrorKey = Object.keys(fieldErrors || {}).find((key) => key.startsWith('recipients'))
  if (recipientErrorKey) {
    recipientError.value = fieldErrors[recipientErrorKey][0]
    return
  }

  formError.value = meta?.error_message || 'We could not send this form right now. Please try again.'
}
</script>

<style scoped lang="scss">
// Vuetify has no height props for a chip field: give it a roomy minimum, and past about five
// rows of chips scroll inside the field instead of growing the dialog with every address added.
.recipients-field {
  :deep(.v-field__input) {
    min-height: 100px;
    max-height: 130px;
    overflow-y: auto;
    align-content: flex-start;
  }
}
</style>
